import { type ReactNode, useEffect, useRef, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, type LucideIcon } from 'lucide-react';
import { useI18n } from '@/shared/i18n';
import { tapPress } from '@/shared/utils/motion';

export type HomeServiceItem = {
  title: string;
  desc: string;
  icon: LucideIcon;
  slug: string;
};

export type HomeCaseItem = {
  title: string;
  metric: string;
  desc: string;
  tag: string;
  icon: LucideIcon;
  image?: string;
  imageClassName?: string;
};

export type HomeApproachItem = {
  title: string;
  text: string;
  icon: LucideIcon;
};

export type HomeTechPartner = {
  name: string;
};

type MobileHomePageProps = {
  servicesItems: HomeServiceItem[];
  featuredCases: HomeCaseItem[];
  approachItems: HomeApproachItem[];
  techPartners: HomeTechPartner[];
  goToSection: (nextIndex: number) => void;
};

type MobileRevealProps = {
  id?: string;
  children: ReactNode;
  className?: string;
  delay?: number;
  y?: number;
  amount?: number;
};

const mobileEase = [0.76, 0, 0.24, 1] as [number, number, number, number];

const MobileReveal = ({
  id,
  children,
  className,
  delay = 0,
  y = 22,
  amount = 0.12
}: MobileRevealProps) => {
  const prefersReducedMotion = useReducedMotion();
  const elementRef = useRef<HTMLDivElement | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const element = elementRef.current;

    if (!element || prefersReducedMotion) {
      setIsVisible(true);
      return;
    }

    const checkVisibilityNow = () => {
      const rect = element.getBoundingClientRect();
      const viewportHeight =
        window.innerHeight || document.documentElement.clientHeight;

      const visiblePixels =
        Math.min(rect.bottom, viewportHeight) - Math.max(rect.top, 0);

      const requiredPixels = Math.min(rect.height * amount, 80);

      if (visiblePixels >= requiredPixels) {
        setIsVisible(true);
        return true;
      }

      return false;
    };

    const firstFrame = window.requestAnimationFrame(() => {
      const secondFrame = window.requestAnimationFrame(() => {
        if (checkVisibilityNow()) return;

        const observer = new IntersectionObserver(
          entries => {
            const entry = entries[0];

            if (entry.isIntersecting) {
              setIsVisible(true);
              observer.disconnect();
            }
          },
          {
            threshold: amount,
            rootMargin: '0px 0px -8% 0px'
          }
        );

        observer.observe(element);
      });

      return () => window.cancelAnimationFrame(secondFrame);
    });

    return () => {
      window.cancelAnimationFrame(firstFrame);
    };
  }, [amount, prefersReducedMotion]);

  return (
    <motion.div
      id={id}
      ref={elementRef}
      className={className}
      initial={prefersReducedMotion ? false : { opacity: 0, y }}
      animate={
        isVisible || prefersReducedMotion
          ? { opacity: 1, y: 0 }
          : { opacity: 0, y }
      }
      transition={{
        duration: prefersReducedMotion ? 0 : 0.55,
        ease: mobileEase,
        delay
      }}
    >
      {children}
    </motion.div>
  );
};

const MobileHomePage = ({
  servicesItems,
  featuredCases,
  approachItems,
  techPartners,
  goToSection
}: MobileHomePageProps) => {
  const { t } = useI18n();
  const prefersReducedMotion = useReducedMotion();

  const getCardAnimation = (index: number) => ({
    initial: prefersReducedMotion ? false : { opacity: 0, y: 22, scale: 0.96 },
    whileInView: prefersReducedMotion
      ? undefined
      : {
          opacity: [0, 1, 1, 1],
          y: [22, 8, -3, 0],
          scale: [0.96, 0.9, 1.04, 1]
        },
    viewport: { once: true, amount: 0.12 },
    transition: {
      duration: prefersReducedMotion ? 0 : 0.84,
      ease: [0.22, 1, 0.36, 1] as [number, number, number, number],
      times: [0, 0.36, 0.72, 1],
      delay: prefersReducedMotion ? 0 : index * 0.1
    }
  });

  return (
    <section className="relative min-h-screen overflow-hidden px-5 pb-16 pt-28 text-[#101726] dark:text-white md:hidden">
      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(160deg,#eef5ff_0%,#f7fbff_45%,#e3f0ff_100%)] dark:bg-[linear-gradient(160deg,#070f24_0%,#0a1733_42%,#0a2540_100%)]" />
      <div className="pointer-events-none absolute inset-x-0 top-0 h-[520px] bg-[radial-gradient(circle_at_50%_-10%,rgba(0,187,195,0.14),transparent_55%)] dark:bg-[radial-gradient(circle_at_50%_-10%,rgba(0,187,195,0.22),transparent_55%)]" />
      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(rgba(14,27,52,.04)_1px,transparent_1px),linear-gradient(90deg,rgba(14,27,52,.04)_1px,transparent_1px)] bg-[size:96px_96px] dark:bg-[linear-gradient(rgba(255,255,255,.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,.02)_1px,transparent_1px)]" />

      <div className="relative z-10 mx-auto flex w-full max-w-6xl flex-col gap-16">
        <MobileReveal
          id="mobile-home"
          className="scroll-mt-28 space-y-5"
          amount={0.02}
        >
          <div className="space-y-3">
            <span className="block text-[10px] font-black uppercase tracking-[0.26em] text-[#007d9b] dark:text-[#00bbc3]">
              {t.home.hero.eyebrow}
            </span>

            <h1 className="text-balance text-[clamp(2.35rem,10vw,5.2rem)] font-black leading-[1.02] tracking-tight text-[#101726] dark:text-white">
              {t.home.hero.titleLine1}
              <br />
              {t.home.hero.titleLine2}{' '}
              <span className="bg-gradient-to-r from-[#007d9b] to-[#00bbc3] bg-clip-text text-transparent dark:from-[#00bbc3] dark:to-[#00bbc3]">
                {t.home.hero.titleHighlight}
              </span>
            </h1>
          </div>

          <p className="max-w-2xl text-sm font-medium leading-7 text-[#263246]/90 dark:text-white/80">
            {t.home.hero.description}
          </p>

          <div className="flex flex-col gap-3 pt-1">
            <Link
              to="/contact"
              className="inline-flex h-12 items-center justify-center gap-2 rounded-xl bg-[#007d9b] px-5 text-sm font-black text-white shadow-lg shadow-[#00bbc3]/20 transition-all active:scale-95 dark:bg-gradient-to-r dark:from-[#007d9b] dark:to-[#00bbc3]"
            >
              {t.common.buttons.bookIntroduction} <ArrowRight size={18} />
            </Link>

            <button
              type="button"
              onClick={() => goToSection(1)}
              className="inline-flex h-12 items-center justify-center gap-2 rounded-xl border border-border-primary bg-background-card/40 px-5 text-sm font-black text-[#007d9b] transition-all dark:text-[#00bbc3]"
            >
              {t.common.buttons.discoverExpertise} <ArrowRight size={18} />
            </button>
          </div>
        </MobileReveal>

        <MobileReveal id="mobile-services" className="scroll-mt-28 space-y-5">
          <div className="space-y-2">
            <span className="text-[10px] font-black uppercase tracking-[0.26em] text-[#007d9b] dark:text-[#00bbc3]">
              {t.home.services.eyebrow}
            </span>
            <h2 className="text-[clamp(2rem,9vw,3.5rem)] font-black leading-none tracking-tight">
              {t.home.services.title}
            </h2>
          </div>

          <div className="grid grid-cols-2 gap-3">
            {servicesItems.map((service, index) => {
              const IconComponent = service.icon;

              return (
                <motion.div key={service.slug} {...getCardAnimation(index)}>
                  <Link to={`/services/${service.slug}`} className="block">
                    <motion.article
                      whileTap={tapPress}
                      className="group flex min-h-[164px] cursor-pointer flex-col justify-between rounded-2xl border border-border-primary bg-background-card/60 p-4 shadow-xl backdrop-blur-xl transition-all dark:border-white/10 dark:bg-white/5"
                    >
                      <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#00bbc3]/10 text-[#007d9b] dark:text-[#00bbc3]">
                        <IconComponent size={19} strokeWidth={2.5} />
                      </span>

                      <div className="mt-3 space-y-1.5">
                        <h3 className="text-[15px] font-black leading-snug tracking-tight">
                          {service.title}
                        </h3>
                        <p className="text-[10px] font-medium leading-[1.45] text-text-secondary">
                          {service.desc}
                        </p>
                      </div>
                    </motion.article>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </MobileReveal>

        <MobileReveal id="mobile-cases" className="scroll-mt-28 space-y-5">
          <div className="grid items-end gap-3 border-b border-border-primary/60 pb-4 dark:border-white/10">
            <h2 className="text-[clamp(1.95rem,8.8vw,3.8rem)] font-black leading-none tracking-tight text-[#101726] dark:text-white">
              {t.home.cases.titleStart}{' '}
              <span className="bg-gradient-to-r from-[#00bbc3] to-cyan-400 bg-clip-text text-transparent">
                {t.home.cases.titleHighlight}
              </span>
            </h2>

            <p className="text-[11px] font-medium leading-5 text-[#263246]/80 dark:text-white/60">
              {t.home.cases.description}
            </p>
          </div>

          <motion.div className="grid grid-cols-1 gap-3" whileTap={tapPress}>
            {featuredCases.map((item, index) => {
              const IconComponent = item.icon;

              return (
                <motion.div
                  key={item.title}
                  {...getCardAnimation(index)}
                  className="group flex flex-col justify-between overflow-hidden rounded-2xl border border-border-primary bg-background-card/40 shadow-xl backdrop-blur-md transition-all dark:border-white/5 dark:bg-white/[0.02]"
                >
                  <div className="relative flex h-32 items-center justify-center overflow-hidden border-b border-border-primary/50 bg-white/85 px-5 dark:border-white/5 dark:bg-white/[0.06]">
                    <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_50%_15%,rgba(0,187,195,0.16),transparent_60%)]" />
                    {item.image ? (
                      <img
                        src={item.image}
                        alt={item.title}
                        className={`relative h-24 max-h-full w-full max-w-[18rem] object-contain drop-shadow-sm ${item.imageClassName ?? ''}`}
                        loading="lazy"
                      />
                    ) : (
                      <div className="relative flex flex-col items-center gap-3 text-center text-[#007d9b] dark:text-[#00bbc3]">
                        <span className="flex h-14 w-14 items-center justify-center rounded-2xl border border-[#00bbc3]/25 bg-[#00bbc3]/10">
                          <IconComponent size={28} strokeWidth={2.4} />
                        </span>
                        <span className="max-w-[14rem] text-[10px] font-black uppercase tracking-[0.24em]">
                          {item.tag}
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="flex flex-1 flex-col justify-between p-4">
                    <div className="space-y-2">
                      <div className="flex items-start justify-between gap-3">
                        <span className="rounded-full bg-[#007d9b]/10 px-3 py-1 text-[9px] font-black uppercase tracking-widest text-[#007d9b] dark:bg-[#00bbc3]/10 dark:text-[#00bbc3]">
                          {item.tag}
                        </span>
                        <span className="bg-gradient-to-r from-[#007d9b] to-[#00bbc3] bg-clip-text text-lg font-black text-transparent dark:from-cyan-400 dark:to-[#00bbc3]">
                          {item.metric}
                        </span>
                      </div>

                      <h3 className="pt-1 text-[15px] font-black tracking-tight text-[#101726] dark:text-white">
                        {item.title}
                      </h3>

                      <p className="text-[11px] font-medium leading-5 text-text-secondary dark:text-white/50">
                        {item.desc}
                      </p>
                    </div>

                    <div className="pt-3">
                      <Link
                        to="/cases"
                        className="inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-wider text-[#007d9b] transition-colors dark:text-[#00bbc3]"
                      >
                        {t.common.buttons.viewFullCaseStudy}{' '}
                        <ArrowRight size={13} strokeWidth={2.5} />
                      </Link>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>

          <Link
            to="/cases"
            className="inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-[#007d9b] px-5 text-xs font-black text-white shadow-lg shadow-[#00bbc3]/20 transition-all active:scale-95 dark:bg-gradient-to-r dark:from-[#007d9b] dark:to-[#00bbc3]"
          >
            {t.common.buttons.browseAllCaseStudies} <ArrowRight size={16} />
          </Link>
        </MobileReveal>

        <MobileReveal id="mobile-approach" className="scroll-mt-28 space-y-5">
          <div className="border-b border-border-primary/60 pb-4 dark:border-white/10">
            <span className="text-[10px] font-black uppercase tracking-[0.26em] text-[#007d9b] dark:text-[#00bbc3]">
              {t.home.approach.eyebrow}
            </span>
            <h2 className="mt-1 text-[clamp(1.65rem,7.2vw,3.5rem)] font-black leading-[1.02] tracking-tight">
              {t.home.approach.titleStart}{' '}
              <span className="bg-gradient-to-r from-[#007d9b] to-[#00bbc3] bg-clip-text text-transparent dark:from-[#00bbc3] dark:to-[#00bbc3]">
                {t.home.approach.titleHighlight}
              </span>
            </h2>
          </div>

          <div className="grid grid-cols-2 gap-3">
            {approachItems.map((item, index) => {
              const ItemIcon = item.icon;

              return (
                <motion.article
                  key={item.title}
                  {...getCardAnimation(index)}
                  whileTap={tapPress}
                  className="flex min-h-[168px] flex-col justify-between rounded-2xl border border-border-primary bg-background-card/50 p-4 shadow-md backdrop-blur-md dark:border-white/5 dark:bg-white/5"
                >
                  <div className="space-y-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-[#00bbc3]/20 bg-[#00bbc3]/10 text-[#007d9b] dark:text-[#00bbc3]">
                      <ItemIcon size={18} strokeWidth={2.5} />
                    </span>

                    <div className="space-y-1.5">
                      <h3 className="text-[13px] font-black leading-snug tracking-tight text-text-primary">
                        {item.title}
                      </h3>
                      <p className="text-[9.5px] font-medium leading-[1.4] text-text-secondary">
                        {item.text}
                      </p>
                    </div>
                  </div>
                </motion.article>
              );
            })}
          </div>

          <Link
            to="/about"
            className="inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-[#007d9b] px-5 text-xs font-black text-white shadow-lg shadow-[#00bbc3]/20 transition-all active:scale-95 dark:bg-gradient-to-r dark:from-[#007d9b] dark:to-[#00bbc3]"
          >
            {t.common.buttons.seeEngineeringStandards} <ArrowRight size={16} />
          </Link>
        </MobileReveal>

        <MobileReveal id="mobile-contact" className="scroll-mt-28 space-y-6">
          <div className="space-y-4">
            <span className="text-[10px] font-black uppercase tracking-[0.26em] text-[#007d9b] dark:text-[#00bbc3]">
              {t.home.cta.eyebrow}
            </span>

            <h2 className="text-[clamp(2.15rem,9.8vw,3.8rem)] font-black leading-[1.05] tracking-tight">
              {t.home.cta.titleLine1}
              <br /> {t.home.cta.titleLine2}{' '}
              <span className="bg-gradient-to-r from-[#007d9b] to-[#00bbc3] bg-clip-text text-transparent dark:from-[#00bbc3] dark:to-[#00bbc3]">
                {t.home.cta.titleHighlight}
              </span>
              {t.home.cta.titleSuffix}
            </h2>

            <p className="max-w-md text-sm font-medium leading-6 text-text-secondary">
              {t.home.cta.description}
            </p>

            <Link
              to="/contact"
              className="inline-flex h-12 items-center justify-center gap-3 rounded-xl bg-[#007d9b] px-6 text-sm font-black text-white shadow-xl shadow-[#00bbc3]/20 transition-all active:scale-95 dark:bg-gradient-to-r dark:from-[#007d9b] dark:to-[#00bbc3]"
            >
              {t.common.buttons.bookIntroduction} <ArrowRight size={18} />
            </Link>
          </div>

          <motion.div className="grid grid-cols-2 gap-3" whileTap={tapPress}>
            {techPartners.map((tech, index) => (
              <motion.div
                key={tech.name}
                {...getCardAnimation(index)}
                className="flex h-[4.25rem] items-center justify-center rounded-xl border border-border-primary/80 bg-background-card/40 p-3 text-center text-xs font-black tracking-tight text-text-primary shadow-sm backdrop-blur-md transition-all dark:border-white/5 dark:bg-white/1"
              >
                <span className="bg-gradient-to-r from-text-primary via-text-primary to-text-secondary bg-clip-text text-transparent dark:from-white dark:to-white/70">
                  {tech.name}
                </span>
              </motion.div>
            ))}
          </motion.div>
        </MobileReveal>
      </div>
    </section>
  );
};

export default MobileHomePage;
